import React, { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { fetchAttendees } from "../../../../redux/features/attendeesSlice";
import { organizationsSliceSelector } from "../../../../redux/features/organizationsSlice";
import moment from "moment";

const PAGE_SIZE = 25;

const EMPTY_ATTENDEES_ARRAY = Object.freeze([]);

const TABLE_MIN_WIDTH = 180 + 120 + 70 + 90 + 80 + 65 + 85 + 85 + 110;

const COLUMNS = [
  {
    id: "email",
    label: "Email",
    field: "email",
    align: "left",
    width: 180,
    minWidth: 140,
    truncate: true,
  },
  {
    id: "displayName",
    label: "Name",
    field: "displayName",
    align: "left",
    width: 120,
    minWidth: 90,
    truncate: true,
  },
  {
    id: "zipcode",
    label: "Zipcode",
    field: "zipcode",
    align: "left",
    width: 70,
    minWidth: 60,
    noWrap: true,
  },
  {
    id: "ticketCountAsPurchaser",
    label: "Tickets (purchased)",
    field: "ticketCountAsPurchaser",
    align: "right",
    width: 90,
    minWidth: 70,
    noWrap: true,
  },
  {
    id: "ticketCountAsAttendeeOnly",
    label: "Tickets (other)",
    field: "ticketCountAsAttendeeOnly",
    align: "right",
    width: 80,
    minWidth: 65,
    noWrap: true,
  },
  {
    id: "eventCountTotal",
    label: "Events",
    field: "eventCountTotal",
    align: "right",
    width: 65,
    minWidth: 52,
    noWrap: true,
  },
  {
    id: "totalSpendUsdCents",
    label: "Spend",
    field: "totalSpendUsdCents",
    align: "right",
    width: 85,
    minWidth: 68,
    noWrap: true,
  },
  {
    id: "checkedInCountTotal",
    label: "Checked in",
    field: "checkedInCountTotal",
    align: "right",
    width: 85,
    minWidth: 68,
    noWrap: true,
  },
  {
    id: "lastSeenAt",
    label: "Last seen",
    field: "lastSeenAt",
    align: "left",
    width: 110,
    minWidth: 88,
    noWrap: true,
  },
];

function formatCents(cents) {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((cents || 0) / 100);
}

function formatLocation(attendee) {
  return attendee?.zipcode || "—";
}

function parseDate(value) {
  if (value == null) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  return null;
}

function formatLastSeen(value) {
  const d = parseDate(value);
  return d ? moment(d).format("MMM D, YYYY") : "—";
}

export default function AttendeesList() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const pathname = location.pathname;
  const orgState = useSelector((state) => organizationsSliceSelector(state));
  const currentOrg = orgState?.currentOrganizationId
    ? orgState.organizationsMap?.[orgState.currentOrganizationId]
    : null;
  const effectiveOrgId = organizationId || currentOrg?.id;

  const [sortBy, setSortBy] = useState("lastSeenAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const attendees = useSelector(
    (state) =>
      state.attendees?.byOrganization?.[effectiveOrgId] ??
      EMPTY_ATTENDEES_ARRAY,
  );
  const loading = useSelector(
    (state) => state.attendees?.loading?.[effectiveOrgId],
  );
  const error = useSelector(
    (state) => state.attendees?.error?.[effectiveOrgId],
  );
  const lastVisible = useSelector(
    (state) => state.attendees?.lastVisibleAttendees?.[effectiveOrgId],
  );
  const allAttendeesLoaded = useSelector(
    (state) => state.attendees?.allAttendeesLoaded?.[effectiveOrgId],
  );
  const loadMoreSentinelRef = useRef(null);
  const hasRequestedInitialLoadRef = useRef(false);

  useEffect(() => {
    hasRequestedInitialLoadRef.current = false;
  }, [effectiveOrgId]);

  // Reset so we refetch when user returns to the Attendees tab (pathname changes back to attendees).
  useEffect(() => {
    if (pathname.includes("/attendees")) {
      hasRequestedInitialLoadRef.current = false;
    }
  }, [pathname]);

  useEffect(() => {
    if (!effectiveOrgId || loading) return;
    // Only run initial fetch once per visit (ref is reset when pathname changes to /attendees or org changes).
    if (hasRequestedInitialLoadRef.current) return;
    hasRequestedInitialLoadRef.current = true;
    dispatch(
      fetchAttendees({
        organizationId: effectiveOrgId,
        pageSize: PAGE_SIZE,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    dispatch,
    effectiveOrgId,
    pathname,
    loading,
    sortBy,
    sortOrder,
  ]);

  const loadMoreAttendees = useCallback(() => {
    if (!effectiveOrgId || loading || allAttendeesLoaded) return;
    dispatch(
      fetchAttendees({
        organizationId: effectiveOrgId,
        pageSize: PAGE_SIZE,
        lastVisible,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    effectiveOrgId,
    loading,
    allAttendeesLoaded,
    dispatch,
    lastVisible,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (!effectiveOrgId || loading || allAttendeesLoaded) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreAttendees();
        }
      },
      { rootMargin: "200px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    effectiveOrgId,
    loading,
    allAttendeesLoaded,
    loadMoreAttendees,
  ]);

  const handleSort = useCallback(
    (field) => {
      const isAsc = sortBy === field && sortOrder === "asc";
      const nextOrder = isAsc ? "desc" : "asc";
      setSortBy(field);
      setSortOrder(nextOrder);
      dispatch(
        fetchAttendees({
          organizationId: effectiveOrgId,
          pageSize: PAGE_SIZE,
          sortBy: field,
          sortOrder: nextOrder,
        }),
      );
    },
    [sortBy, sortOrder, dispatch, effectiveOrgId],
  );

  if (!effectiveOrgId) {
    return (
      <Typography color="text.secondary">
        Select an organization to view attendees.
      </Typography>
    );
  }

  if (loading && attendees.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (attendees.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        No attendees yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          aria-label="Attendees"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: TABLE_MIN_WIDTH,
            "& .MuiTableCell-root": { px: 0.5, py: 0.75 },
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
<TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    ...(col.width && {
                      width: col.width,
                      maxWidth: col.width,
                    }),
                    ...(col.minWidth && { minWidth: col.minWidth }),
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === col.field}
                    direction={sortBy === col.field ? sortOrder : "asc"}
                    onClick={() => handleSort(col.field)}
                  >
                    <Typography variant="subtitle2" component="span">
                      {col.label}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {attendees.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`${row.id}`)}
              >
                {COLUMNS.map((col) => {
                  let content;
                  if (col.field === "email") content = row.email;
                  else if (col.field === "displayName")
                    content = row.displayName || "—";
                  else if (col.field === "zipcode") content = formatLocation(row);
                  else if (col.field === "ticketCountAsPurchaser")
                    content = row.ticketCountAsPurchaser ?? 0;
                  else if (col.field === "ticketCountAsAttendeeOnly")
                    content = row.ticketCountAsAttendeeOnly ?? 0;
                  else if (col.field === "eventCountTotal")
                    content = row.eventCountTotal ?? 0;
                  else if (col.field === "totalSpendUsdCents")
                    content = formatCents(row.totalSpendUsdCents);
                  else if (col.field === "checkedInCountTotal")
                    content = row.checkedInCountTotal ?? 0;
                  else if (col.field === "lastSeenAt")
                    content = formatLastSeen(row.lastSeenAt);
                  else content = row[col.field] ?? "—";
                  return (
                    <TableCell
                      key={col.id}
                      align={col.align}
                      sx={{
                        ...(col.width && {
                          width: col.width,
                          maxWidth: col.width,
                        }),
                        ...(col.minWidth && { minWidth: col.minWidth }),
                        ...(col.noWrap && { whiteSpace: "nowrap" }),
                        ...(col.truncate && {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }),
                      }}
                    >
                      {col.truncate && content != null && String(content).trim() !== "" ? (
                        <Tooltip title={String(content)} placement="top-start" enterDelay={300}>
                          <span
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {content}
                          </span>
                        </Tooltip>
                      ) : (
                        content
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!allAttendeesLoaded && attendees.length > 0 && (
        <Box
          ref={loadMoreSentinelRef}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 48,
            py: 2,
          }}
        >
          {loading && <CircularProgress size={24} />}
        </Box>
      )}
    </Box>
  );
}
